import { config } from "../config";
import { vendingService } from "../services/vendingService";
import { walletService } from "../services/walletService";
import { transactionService } from "../services/transactionService";
import { appStore } from "../state/store";
import { byId, qsAll } from "./dom";
import { createStepController } from "./steps";
import { formatDateTime, formatNaira, toKobo } from "./format";
import type { Meter } from "../types";

const DEMO_NOTE = "Demo mode: this token is simulated and is not valid on a live meter.";
const PRACTICE_NOTE = "PRACTICE MODE: this token was simulated by the app and did not come from the vending provider. It will not work on any meter.";
const TEST_NOTE = "TEST MODE: this token comes from the vending provider's test system and will not work on a real meter.";

async function refreshAfterPending(): Promise<void> {
  try {
    const [wallet, txPage] = await Promise.all([walletService.getBalance(), transactionService.listTransactions({ limit: 20 })]);
    appStore.setWallet(wallet);
    appStore.setRecentTransactions(txPage.items);
  } catch {
    /* the next normal refresh will catch up */
  }
}

export function initBuyElectricityFlow(): void {
  const root = byId("vend-flow");
  const steps = createStepController(root);

  const meterSelect = byId<HTMLSelectElement>("buy-meter-select");
  const meterEmptyNote = byId("buy-meter-empty-note");

  const voucherChips = qsAll<HTMLButtonElement>(".voucher-chip");
  const customAmountInput = byId<HTMLInputElement>("custom-amount");
  const continueBtn = byId<HTMLButtonElement>("buy-continue-btn");
  const selectError = byId("buy-select-error");

  const reviewMeter = byId("buy-review-meter");
  const reviewAmount = byId("buy-review-amount");
  const confirmBtn = byId<HTMLButtonElement>("buy-confirm-btn");
  const backBtn = byId("buy-back-btn");

  const tokenDigitsEl = byId("token-digits");
  const tokenMeterEl = byId("token-meter-meta");
  const tokenAmountMeta = byId("token-amount-meta");
  const tokenReferenceEl = byId("token-reference-meta");
  const tokenTimeMeta = byId("token-time-meta");
  const copyTokenBtn = byId<HTMLButtonElement>("copy-token-btn");
  const buyDoneBtn = byId("buy-done-btn");

  const failureReason = byId("buy-failure-reason");
  const retryBtn = byId("buy-retry-btn");
  const cancelFromFailBtn = byId("buy-cancel-btn");

  const heldMessage = byId("buy-held-message");
  const heldDoneBtn = byId("buy-held-done-btn");
  const disclaimerEl = byId("token-disclaimer");

  const vendWalletWarningEl = byId("vend-wallet-warning");
  const vendFundShortcutBtn = byId("vend-fund-shortcut-btn");

  let selectedAmount = 5000;
  let onNeedFunds: (() => void) | null = null;

  function setOnNeedFunds(fn: () => void): void {
    onNeedFunds = fn;
  }

  function populateMeters(meters: Meter[]): void {
    const previousValue = meterSelect.value;
    meterSelect.innerHTML = "";
    meters.forEach((meter) => {
      const opt = document.createElement("option");
      opt.value = meter.id;
      opt.textContent = `${meter.label} \u00b7 ${meter.serial}`;
      meterSelect.appendChild(opt);
    });
    if (meters.some((m) => m.id === previousValue)) meterSelect.value = previousValue;

    const hasMeters = meters.length > 0;
    meterSelect.disabled = !hasMeters;
    continueBtn.disabled = !hasMeters;
    meterEmptyNote.hidden = hasMeters;
  }

  appStore.subscribe((state) => populateMeters(state.meters));

  voucherChips.forEach((chip) => {
    chip.addEventListener("click", () => {
      voucherChips.forEach((c) => c.classList.remove("is-selected"));
      chip.classList.add("is-selected");
      if (chip.dataset.amount === "custom") {
        customAmountInput.hidden = false;
        customAmountInput.focus();
        selectedAmount = Number(customAmountInput.value) || 0;
      } else {
        customAmountInput.hidden = true;
        selectedAmount = Number(chip.dataset.amount);
      }
    });
  });

  customAmountInput.addEventListener("input", () => {
    selectedAmount = Number(customAmountInput.value) || 0;
  });

  function hideWalletWarning(): void {
    vendWalletWarningEl.hidden = true;
  }

  function reset(): void {
    hideWalletWarning();
    selectError.hidden = true;
    steps.show("select");
  }

  continueBtn.addEventListener("click", () => {
    hideWalletWarning();
    selectError.hidden = true;

    if (!meterSelect.value) {
      selectError.textContent = "Select a meter to buy for.";
      selectError.hidden = false;
      return;
    }
    if (!selectedAmount || selectedAmount < 500) {
      customAmountInput.hidden = false;
      customAmountInput.placeholder = "Enter at least \u20a6500";
      customAmountInput.focus();
      return;
    }

    const balance = appStore.state.wallet?.balanceKobo ?? 0;
    if (toKobo(selectedAmount) > balance) {
      vendWalletWarningEl.hidden = false;
      vendWalletWarningEl.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      return;
    }

    const meter = appStore.state.meters.find((m) => m.id === meterSelect.value);
    reviewMeter.textContent = meter ? `${meter.label} \u00b7 ${meter.serial}` : meterSelect.value;
    reviewAmount.textContent = `\u20a6${selectedAmount.toLocaleString("en-NG")}`;
    steps.show("review");
  });

  backBtn.addEventListener("click", () => steps.show("select"));
  cancelFromFailBtn.addEventListener("click", reset);

  vendFundShortcutBtn.addEventListener("click", () => {
    hideWalletWarning();
    onNeedFunds?.();
  });

  confirmBtn.addEventListener("click", async () => {
    steps.show("pending");
    const meterId = meterSelect.value;
    const amountKobo = toKobo(selectedAmount);

    try {
      const result = await vendingService.buyElectricity({ meterId, amountKobo });
      if (result.status === "success" && result.token) {
        const balance = appStore.state.wallet;
        if (balance) {
          appStore.setWallet({ ...balance, balanceKobo: balance.balanceKobo - amountKobo, updatedAt: new Date().toISOString() });
        }
        appStore.prependTransaction({
          id: result.token.transactionReference,
          type: "electricity_purchase",
          status: "success",
          amountKobo: result.token.amountKobo,
          direction: "debit",
          description: "Electricity Purchase",
          reference: result.token.transactionReference,
          createdAt: result.token.issuedAt,
          meterId: result.token.meterId,
          meterSerial: result.token.meterSerial,
          units: result.token.units,
        });

        tokenDigitsEl.textContent = result.token.token;
        tokenMeterEl.textContent = `Meter ${result.token.meterSerial}`;
        tokenAmountMeta.textContent = result.token.units
          ? `\u20a6${formatNaira(result.token.amountKobo)} \u00b7 ${result.token.units}`
          : `\u20a6${formatNaira(result.token.amountKobo)}`;
        tokenReferenceEl.textContent = result.token.transactionReference;
        tokenTimeMeta.textContent = formatDateTime(result.token.issuedAt);
        disclaimerEl.textContent = config.useMockServices
          ? DEMO_NOTE
          : result.token.practice
            ? PRACTICE_NOTE
            : result.token.testMode
              ? TEST_NOTE
            : "Enter this token on your meter's keypad to load the credit.";
        steps.show("success");
      } else if (result.status === "pending") {
        // Charged, but the provider's answer is uncertain: show the hold message and
        // re-read the real wallet + history instead of guessing what changed.
        heldMessage.textContent = result.message ?? "We're confirming this purchase.";
        steps.show("held");
        void refreshAfterPending();
      } else {
        failureReason.textContent = result.failureReason ?? "This purchase could not be completed.";
        steps.show("failed");
      }
    } catch {
      failureReason.textContent = "Something went wrong. Please try again.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("select"));
  buyDoneBtn.addEventListener("click", reset);
  heldDoneBtn.addEventListener("click", reset);

  copyTokenBtn.addEventListener("click", async () => {
    const raw = (tokenDigitsEl.textContent ?? "").split("\n").map((t) => t.replace(/\s/g, "")).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(raw);
      const original = copyTokenBtn.textContent;
      copyTokenBtn.textContent = "Copied";
      setTimeout(() => {
        copyTokenBtn.textContent = original;
      }, 1600);
    } catch {
      copyTokenBtn.textContent = "Copy failed";
      setTimeout(() => {
        copyTokenBtn.textContent = "Copy Token";
      }, 1600);
    }
  });

  reset();
  populateMeters(appStore.state.meters);

  buyElectricityFlowRef.setOnNeedFunds = setOnNeedFunds;
}

// Small escape hatch so appShell can wire "fund wallet" shortcut without a
// full pub/sub for a single callback.
export const buyElectricityFlowRef: { setOnNeedFunds: ((fn: () => void) => void) | null } = {
  setOnNeedFunds: null,
};
